<template>
  <OnboardingLayout
    :is-next-allowed="answer != null"
    :is-next-loading="isNextLoading"
    :is-prev-button-shown="true"
    :is-end="isEnd"
    @next-step="$emit('nextStep')"
    @prev-step="$emit('prevStep')"
  >
    <template #left>
      <h1>How do you plan to use Routine?</h1>
      <h2>Do not worry, this will not limit you in any way.</h2>
      <div class="blocks">
        <div
          class="block lg"
          :class="{ isSelected: answer === 'individual' }"
          @mouseenter="showImg('individual')"
          @mouseleave="showImg()"
          @click="$emit('update:answer', 'individual')"
        >
          <div class="icon">
            <OnboardingIndividualIcon />
          </div>
          <div class="content">
            <h3>For myself</h3>
            <p>
              I want a personal space to organize my work and life: calendars,
              tasks, notes & projects.
            </p>
          </div>
        </div>
        <div
          class="block lg"
          :class="{ isSelected: answer === 'team' }"
          @mouseenter="showImg('team')"
          @mouseleave="showImg()"
          @click="$emit('update:answer', 'team')"
        >
          <div class="icon">
            <OnboardingTeamIcon />
          </div>
          <div class="content">
            <h3>With my team</h3>
            <p>
              I want a simple yet powerful home to organize my team's projects,
              knowledge, meetings, clients and more.
            </p>
          </div>
        </div>
      </div>
    </template>
    <template #right>
      <img :src="images[imgName || 'empty']" />
    </template>
  </OnboardingLayout>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, toRefs, watch } from "vue";

import OnboardingIndividualIcon from "@/components/icons/onboarding/IndividualIcon.vue";
import OnboardingTeamIcon from "@/components/icons/onboarding/TeamIcon.vue";
import OnboardingLayout from "@/components/onboarding/OnboardingLayout.vue";
import { metrics } from "@/metrics";

export type UseCaseType = "individual" | "team" | undefined;

export default defineComponent({
  components: {
    OnboardingTeamIcon,
    OnboardingIndividualIcon,
    OnboardingLayout,
  },
  props: {
    answer: {
      type: String as PropType<UseCaseType>,
      required: false,
      default: undefined,
    },
    isNextLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Whether the next click ends onboarding and launches the app (true) or
     * another step follows (false) — e.g. the plan step follows the
     * individual path. Decides the button label: Launch vs Continue.
     */
    isEnd: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: {
    nextStep: () => true,
    prevStep: () => true,
    "update:answer": (_n: UseCaseType) => true,
  },
  setup(props) {
    const { answer } = toRefs(props);
    type ImageKey = "empty" | "individual" | "team";
    const imgName = ref<ImageKey | undefined>(answer.value);
    watch(answer, (v) => {
      imgName.value = v;
    });
    const showImg = (name?: ImageKey) => {
      imgName.value = name || answer.value;
    };
    const images: Record<ImageKey, string> = {
      empty: new URL(`../../assets/onboarding/step_agenda.png`, import.meta.url)
        .href,
      individual: new URL(
        `../../assets/onboarding/step_calendar.png`,
        import.meta.url
      ).href,
      team: new URL(`../../assets/onboarding/step_project.png`, import.meta.url)
        .href,
    };
    metrics.segment.page("Onboarding", { step: "usecase" });
    return {
      imgName,
      showImg,
      images,
    };
  },
});
</script>

<style scoped lang="scss">
.blocks {
  .block.lg {
    .content {
      p {
        padding-top: 12px;
      }
    }
  }
}
</style>
